import { captureMessage } from '@/lib/sentry'

const STORAGE_KEY = 'dextoro_login_attempts'
const LOCKOUT_THRESHOLD = 5
const LOCKOUT_WINDOW_MS = 15 * 60 * 1000 // 15 minutes
const LOCKOUT_DURATION_MS = 30 * 60 * 1000 // 30 minutes

interface LoginAttempt {
  email: string
  timestamp: number
}

function getAttempts(email: string): LoginAttempt[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const all: LoginAttempt[] = JSON.parse(raw)
    const cutoff = Date.now() - LOCKOUT_WINDOW_MS
    return all.filter((a) => a.email === email && a.timestamp > cutoff)
  } catch {
    return []
  }
}

export function recordFailedAttempt(email: string): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const all: LoginAttempt[] = raw ? JSON.parse(raw) : []
    all.push({ email, timestamp: Date.now() })
    // Prune old entries (older than lockout duration)
    const cutoff = Date.now() - LOCKOUT_DURATION_MS
    const pruned = all.filter((a) => a.timestamp > cutoff)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned))
  } catch {
    // localStorage may be unavailable
  }
}

export function clearAttempts(email: string): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const all: LoginAttempt[] = JSON.parse(raw)
    const filtered = all.filter((a) => a.email !== email)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered))
  } catch {
    // localStorage may be unavailable
  }
}

export function isLockedOut(email: string): { locked: boolean; remainingMinutes: number } {
  const attempts = getAttempts(email)
  if (attempts.length < LOCKOUT_THRESHOLD) {
    return { locked: false, remainingMinutes: 0 }
  }

  const lastAttempt = Math.max(...attempts.map((a) => a.timestamp))
  const lockoutEnd = lastAttempt + LOCKOUT_DURATION_MS
  const remaining = lockoutEnd - Date.now()

  if (remaining <= 0) {
    return { locked: false, remainingMinutes: 0 }
  }

  captureMessage(
    `Login lockout triggered: ${attempts.length} failed attempts`,
    'warning'
  )

  return {
    locked: true,
    remainingMinutes: Math.ceil(remaining / (60 * 1000)),
  }
}
